import { useEffect, useState } from "react";
import { Beneficiary } from "components/views/PrescriptionDetails/Patient";
import { getUserByCardNumber } from "services/api/user/userService";

export type UseBeneficiariesResult = {
    beneficiaries: Array<Beneficiary>;
    isLoadingBeneficiaries: boolean;
};

// Busca os beneficiários (titular + dependentes) do protocolo atual.
// Reseta a lista sempre que o cartão/cliente mudam e ignora respostas de
// requisições obsoletas, evitando que dados de um protocolo anterior
// permaneçam em tela ao navegar entre protocolos (JAR-7180). Expõe
// `isLoadingBeneficiaries` para que a tela possa distinguir "carregando"
// de "nenhum beneficiário vinculado ao protocolo".
export function useBeneficiaries(cardNumber: string, customerId: string): UseBeneficiariesResult {
    const [beneficiaries, setBeneficiaries] = useState<Array<Beneficiary>>([]);
    const [isLoadingBeneficiaries, setIsLoadingBeneficiaries] = useState(false);

    useEffect(() => {
        let cancelled = false;

        setBeneficiaries([]);
        setIsLoadingBeneficiaries(!!cardNumber);

        async function loadBeneficiaries() {
            if (!cardNumber) {
                return;
            }

            try {
                const response = await getUserByCardNumber(cardNumber);
                if (cancelled) return;

                const userData = response.data.data;
                const { name, cardNumber: beneficiaryCardNumber, dependents } = userData;

                const dependentsModel = dependents?.map((dependent) => ({
                    name: dependent.name,
                    cardNumber: dependent.cardNumber,
                })) || [];

                setBeneficiaries([{ name, cardNumber: beneficiaryCardNumber }, ...dependentsModel]);
            } catch (err) {
                if (cancelled) return;

                console.error(err);
                setBeneficiaries([]);
            } finally {
                if (!cancelled) {
                    setIsLoadingBeneficiaries(false);
                }
            }
        }

        loadBeneficiaries();

        return () => {
            cancelled = true;
        };
    }, [cardNumber, customerId]);

    return { beneficiaries, isLoadingBeneficiaries };
}
